// popup_partner.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import Draggable from 'react-draggable'
import partnerImg from '../../Image/popup/partner.png'

export default function PartnerAdPopup({
    days = 7,
    storageKey = 'nr_partner_popup_hide_until',
    showDelayMs = 10,
    partnerLink = '/Signin',
}) {
    const [open, setOpen] = useState(false)

    // 위치/사이즈 상태 (컨트롤드)
    const [pos, setPos] = useState({ x: 0, y: 120 })
    const [size, setSize] = useState({ width: 500, height: 770 })

    // 우하단 리사이즈 드래그 상태
    const resizing = useRef(false)
    const startMouse = useRef({ x: 0, y: 0 })
    const startSize = useRef({ width: 500, height: 420 })

    // 처음 열릴지
    useEffect(() => {
        try {
            const ts = localStorage.getItem(storageKey)
            const hideUntil = ts ? Number(ts) : 0
            if (Date.now() > hideUntil) {
                const t = setTimeout(() => setOpen(true), showDelayMs)
                return () => clearTimeout(t)
            }
        } catch (e) {
            setOpen(true)
        }
    }, [storageKey, showDelayMs])

    const closeNow = useCallback(() => setOpen(false), [])
    const closeForDays = useCallback(() => {
        try {
            const ms = days * 24 * 60 * 60 * 1000
            localStorage.setItem(storageKey, String(Date.now() + ms))
        } catch (e) {}
        setOpen(false)
    }, [days, storageKey])

    // 단축키로 다시 보기
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && (e.key === 'P' || e.key === 'p')) {
                try {
                    localStorage.removeItem(storageKey)
                } catch (e) {}
                setOpen(true)
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [storageKey])

    // 우하단 리사이즈: 바운드 안 넘게 clamp
    useEffect(() => {
        const MARGIN = 0
        const MIN_W = 360
        const MIN_H = 300

        const onMove = (e) => {
            if (!resizing.current) return
            const dx = e.clientX - startMouse.current.x
            const dy = e.clientY - startMouse.current.y

            const vw = window.innerWidth
            const vh = window.innerHeight
            const maxW = Math.max(MIN_W, vw - pos.x - MARGIN)
            const maxH = Math.max(MIN_H, vh - pos.y - MARGIN)

            const nextW = Math.min(Math.max(MIN_W, startSize.current.width + dx), maxW)
            const nextH = Math.min(Math.max(MIN_H, startSize.current.height + dy), maxH)
            setSize({ width: nextW, height: nextH })
        }
        const onUp = () => {
            resizing.current = false
        }

        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
        return () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
    }, [pos.x, pos.y])

    const startResize = (e) => {
        resizing.current = true
        startMouse.current = { x: e.clientX, y: e.clientY }
        startSize.current = { ...size }
        e.stopPropagation()
        e.preventDefault()
    }

    // 창 리사이즈 시 현재 위치 기준으로 사이즈 재클램프
    useEffect(() => {
        const MARGIN = 0
        const MIN_W = 360
        const MIN_H = 300
        const clampToViewport = () => {
            const vw = window.innerWidth
            const vh = window.innerHeight
            const maxW = Math.max(MIN_W, vw - pos.x - MARGIN)
            const maxH = Math.max(MIN_H, vh - pos.y - MARGIN)
            setSize((s) => ({
                width: Math.min(s.width, maxW),
                height: Math.min(s.height, maxH),
            }))
            setPos((p) => ({
                x: Math.min(Math.max(0, p.x), vw - MARGIN - MIN_W),
                y: Math.min(Math.max(0, p.y), vh - MARGIN - MIN_H),
            }))
        }
        window.addEventListener('resize', clampToViewport)
        return () => window.removeEventListener('resize', clampToViewport)
    }, [pos.x, pos.y])

    if (!open) return null

    return (
        <Draggable
            handle=".popup-header"
            position={pos}
            onDrag={(_, data) => setPos({ x: data.x, y: data.y })}
            bounds="body"
        >
            <PopupBox style={{ width: size.width, height: size.height }}>
                <Header className="popup-header">
                    <CloseBtn onClick={closeNow}>×</CloseBtn>
                </Header>

                <Body>
                    <ImgBox>
                        <img src={partnerImg} alt="파트너 모집 배너" />
                    </ImgBox>
                    <CTAWrapper>
                        <CTA href={`${partnerLink}?type=partner`}>파트너 등록 바로가기</CTA>
                        <SkipBtn onClick={closeForDays}>{days}일간 보지 않기</SkipBtn>
                        {/* 추가된 즉시 닫기 버튼 */}
                        <DarkBtn onClick={closeNow}>닫기</DarkBtn>
                    </CTAWrapper>
                </Body>

                {/* 우하단 리사이즈 핸들 */}
                <ResizeHandle onMouseDown={startResize} />
            </PopupBox>
        </Draggable>
    )
}

/* ===== styled ===== */

const PopupBox = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 14px;
    box-shadow: 0 15px 35px rgba(15, 23, 42, 0.15);
    z-index: 1600;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 300px;
`

const Header = styled.div`
    height: 30px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 14px 14px 0 0;
    display: flex;
    align-items: center;
    padding: 0 10px 0 12px;
    font-size: 13px;
    color: #111827;
    cursor: move;
    position: relative;
    flex: 0 0 auto;
`

const CloseBtn = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 26px;
    height: 26px;
    border: none;
    background: transparent;
    font-size: 18px;
    line-height: 1;
    color: #6b7280;
    border-radius: 8px;
    cursor: pointer;
    &:hover {
        background: rgba(0, 0, 0, 0.03);
        color: #111827;
    }
`

const Body = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
`

const ImgBox = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    border-radius: 10px;
    background: #f3f4f6;

    img {
        width: 100%;
        height: auto;
        display: block;
    }
`

const CTAWrapper = styled.div`
    flex: 0 0 auto;
    display: flex;
    gap: 10px;
`

const CTA = styled.a`
    flex: 1;
    text-align: center;
    padding: 11px 14px;
    border-radius: 10px;
    font-weight: 700;
    background: #ffc94c;
    border: 1px solid #e8b200;
    color: #3a2e00;
    text-decoration: none;
`

const SkipBtn = styled.button`
    background: #111;
    color: #fff;
    border: 0;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
`

/* "7일간 보지 않기" 색상과 동일한 버튼 */
const DarkBtn = styled.button`
    background: #111;
    color: #fff;
    border: 0;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
`

const ResizeHandle = styled.div`
    position: absolute;
    right: 3px;
    bottom: 3px;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;
    background: radial-gradient(circle at 100% 100%, rgba(15, 23, 42, 0.35) 0%, transparent 60%);
    border-bottom-right-radius: 12px;
`
